import { DivIcon } from 'leaflet';
import type { Category } from '@map/shared';

export interface CategoryStyle {
  readonly label: string;
  readonly color: string;
  readonly glyph: string;
}

/**
 * Une couleur ET un glyphe par categorie. La couleur seule ne suffit pas :
 * environ 8 % des hommes distinguent mal le rouge du vert, et une carte de
 * reperage se lit souvent en plein soleil sur un telephone.
 *
 * Les teintes sont choisies pour rester distinctes une fois posees sur le
 * corps sombre du marqueur, et pour ne pas se confondre avec le safran de
 * l'accent qui signale, lui, la selection.
 */
const STYLES: Record<Category, CategoryStyle> = {
  ville: { label: 'Ville', color: '#5c93de', glyph: 'V' },
  rando: { label: 'Rando', color: '#8ac44f', glyph: 'R' },
  spot: { label: 'Spot', color: '#e2568f', glyph: 'S' },
  hebergement: { label: 'Hebergement', color: '#a887e6', glyph: 'H' },
  resto: { label: 'Resto', color: '#f0894a', glyph: 'M' },
  transport: { label: 'Transport', color: '#3fc3b4', glyph: 'T' },
  autre: { label: 'Autre', color: '#a2b3b4', glyph: '?' },
};

export function categoryStyle(category: Category): CategoryStyle {
  return STYLES[category];
}

export type ScoreTone = 'neg' | 'zero' | 'pos';

export function scoreTone(score: number): ScoreTone {
  if (score > 0) return 'pos';
  if (score < 0) return 'neg';
  return 'zero';
}

export function scoreLabel(score: number): string {
  return score > 0 ? `+${score}` : String(score);
}

export interface MarkerOptions {
  readonly category: Category;
  readonly score: number;
  readonly selected: boolean;
  /**
   * Rang du lieu dans l'itineraire (1, 2, 3...), ou undefined s'il dort
   * encore au vivier. Une etape retenue porte son numero sur la carte : la
   * polyligne seule ne dit pas dans quel sens on la parcourt.
   */
  readonly step?: number | undefined;
}

/**
 * Le HTML d'un marqueur. Il est volontairement constitue de constantes :
 * couleur, glyphe, score et rang derivent du modele, jamais d'une saisie
 * libre. Leaflet insere cette chaine sans assainissement, un titre de lieu
 * passe ici suffirait a injecter du script. Le rang est repasse par
 * Math.round avant d'etre ecrit, aucune chaine ne traverse cette fonction.
 */
export function markerHtml(options: MarkerOptions): string {
  const style = categoryStyle(options.category);
  const isStep = typeof options.step === 'number' && Number.isFinite(options.step);
  const classes = [
    'map-pin',
    `map-pin--${scoreTone(options.score)}`,
    isStep ? 'map-pin--step' : '',
    options.selected ? 'map-pin--selected' : '',
  ]
    .filter((entry) => entry !== '')
    .join(' ');

  const parts = [
    `<span class="${classes}" style="--pin-color:${style.color}">`,
    `<span class="map-pin__glyph">${style.glyph}</span>`,
  ];
  if (isStep) {
    parts.push(`<span class="map-pin__step">${Math.round(options.step as number)}</span>`);
  }
  parts.push(`<span class="map-pin__score">${scoreLabel(options.score)}</span>`, '</span>');
  return parts.join('');
}

/**
 * Le gabarit laisse deborder le libelle horizontalement : seule la pointe,
 * calee en bas au centre, doit tomber sur la coordonnee exacte.
 */
const PIN_SIZE: [number, number] = [40, 44];
const PIN_ANCHOR: [number, number] = [20, 44];

export function markerIcon(options: MarkerOptions): DivIcon {
  return new DivIcon({
    html: markerHtml(options),
    className: 'map-pin-host',
    iconSize: PIN_SIZE,
    iconAnchor: PIN_ANCHOR,
    popupAnchor: [0, -40],
  });
}
